import * as React from "react";

import { Box, Stack, Typography } from "@mui/material";

import personalRepresentative from "@assets/images/personal-representative.svg";
import { colors } from "../../styles/constants";
import {
  NotifierDetails as NotifierDetailsType,
  Relationship,
  Titles,
} from "../../types/NotifierFormData";
import { BooleanInput } from "../../components/atoms/BooleanInput";
import { DateInput } from "../../components/atoms/DateInput";
import { EmailAddressInput } from "../../components/atoms/EmailAddressInput";
import { Header } from "../../components/atoms/Header";
import { PhoneNumberTextInput } from "../../components/atoms/PhoneNumberTextInput";
import { SelectInput } from "../../components/atoms/SelectInput";
import { TextInput } from "../../components/atoms/TextInput";
import { NewTitleInput } from "../../components/atoms/TitleInput";
import { NewAddressInput } from "../../components/molecules/AddressInput";
import { FormField } from "../../components/molecules/FormField";
import { FormStack } from "../../components/molecules/FormStack";
import { InfoBoxTrigger } from "../../components/molecules/InfoBoxTrigger";
import { RelationToDeceased } from "../../components/molecules/RelationToDeceased";
import { UpdateFormTemplateProc } from "@templates/FormTemplate";
import { IDVerificationDialog } from "./IDVerificationDialog";
import { roleOptions } from "@customTypes/NotifierFormData";

const currentSection = "notifier";

export type NotifierRecord = {
  relationshipToDeceased?: Relationship;
  relationshipToDeceasedOther?: string;
  title?: Titles;
  firstName?: string;
  surname?: string;
  dateOfBirth?: string;
  address?: string;
  city?: string;
  postcode?: string;
  contactNumber?: string;
  email?: string;
  doNotContact?: boolean;
  role?: string;
  isVulnerable?: boolean;
};

export type NotifierRecordErrors = Partial<Record<keyof NotifierDetailsType, string>>;

export type NotifierDetailsViewProps = {
  titleHidden: boolean;
  serviceProvider: { customForm?: string };
  record: NotifierRecord;
  errors: NotifierRecordErrors;

  onRoleChange: (value: string) => void;
  onDateOfBirthChange: (value?: string) => void;
  onNext: () => void;
  onFieldChange: any;
  onSectionClick: (section: string) => void;
  updateTemplate: UpdateFormTemplateProc;
  onIsVulnerableChange: (value: boolean) => void;
  showIDVerificationDialog: boolean;
  hideIDVerificationDialog: () => void;
};

export const NotifierDetailsView: React.FC<NotifierDetailsViewProps> = ({
  titleHidden,
  serviceProvider,
  errors,
  record,

  onRoleChange,
  onDateOfBirthChange,
  onNext,
  onFieldChange,
  onSectionClick,
  onIsVulnerableChange,
  updateTemplate,
  showIDVerificationDialog,
  hideIDVerificationDialog,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy: false,
      currentSection,
      onNext,
      saveLabel: null,
      onSectionClick,
    });
  }, [onNext, onSectionClick, updateTemplate]);

  return (
    <>
      <IDVerificationDialog
        visible={showIDVerificationDialog}
        hideDialog={hideIDVerificationDialog}
      />

      <Stack rowGap={4}>
        <Header level={1} color={colors.accentTeal}>
          Now, let's find out more about you
        </Header>

        <FormStack>
          <FormField
            halfWidthByItself
            label={
              <span>
                I am a personal representative and I am...{" "}
                <InfoBoxTrigger white width="448px" content={renderRepresentativeInfoBox()} />
              </span>
            }
          >
            <SelectInput
              name="role"
              onValueChange={onRoleChange}
              value={record.role || ""}
              options={roleOptions}
              error={errors?.role}
            />
          </FormField>

          {!titleHidden && (
            <FormField halfWidthByItself label="Title">
              <NewTitleInput
                name="title"
                onValueChange={(value) => onFieldChange({ target: { name: "title", value } })}
                value={record.title || ""}
                error={errors?.title}
              />
            </FormField>
          )}

          <FormField halfWidth label="First name">
            <TextInput
              name="firstName"
              value={record.firstName || ""}
              error={errors?.firstName}
              onChange={onFieldChange}
            />
          </FormField>

          <FormField halfWidth label="Last name">
            <TextInput
              name="surname"
              value={record.surname || ""}
              error={errors?.surname}
              onChange={onFieldChange}
            />
          </FormField>

          {record.role !== "solicitor" && (
            <FormField halfWidthByItself label="Date of birth">
              <DateInput
                value={record.dateOfBirth || ""}
                onValueChange={onDateOfBirthChange}
                error={errors?.dateOfBirth}
                pastOnly
              />
            </FormField>
          )}

          <FormField label="Address">
            <NewAddressInput
              name="address"
              value={record.address || ""}
              onChange={onFieldChange}
              error={errors?.address}
            />
          </FormField>

          <FormField halfWidth label="City or Town">
            <TextInput
              name="city"
              value={record.city || ""}
              onChange={onFieldChange}
              error={errors?.city}
            />
          </FormField>

          <FormField halfWidth label="Postcode">
            <TextInput
              name="postcode"
              value={record.postcode || ""}
              onChange={onFieldChange}
              error={errors?.postcode}
            />
          </FormField>

          <FormField halfWidth label="Telephone number">
            <PhoneNumberTextInput
              name="contactNumber"
              value={record.contactNumber || ""}
              onChange={onFieldChange}
              error={errors?.contactNumber}
            />
          </FormField>

          <FormField halfWidth label="Email address">
            <EmailAddressInput
              name="email"
              value={record.email || ""}
              onChange={onFieldChange}
              error={errors?.email}
              // autoFocus={updatingNotifierEmailAddress}
            />
          </FormField>

          {record.role !== "solicitor" && (
            <FormStack substack spacing={0}>
              <FormField>
                <BooleanInput
                  label={
                    <span>
                      I feel vulnerable (temporarily or long-term) and would appreciate support{" "}
                      <InfoBoxTrigger white width="448px" content={renderVulnerableInfoBox()} />
                    </span>
                  }
                  value={record.isVulnerable}
                  onValueChange={onIsVulnerableChange}
                />
              </FormField>

              {serviceProvider.customForm === "portsmouth-water" && record.isVulnerable && (
                <FormField>
                  To notify Portsmouth Water of vulnerability,{" "}
                  <a
                    href="https://www.portsmouthwater.co.uk/customer-services/priority-services/"
                    rel="noreferrer"
                    target="_blank"
                  >
                    click here
                  </a>{" "}
                  to sign up to their priority services register.
                </FormField>
              )}
            </FormStack>
          )}

          {record.role !== "solicitor" && (
            <FormField
              halfWidthByItself
              label={
                <span>
                  The person who died was my... &nbsp; {renderIntestacyInfoBox(serviceProvider)}
                </span>
              }
            >
              <br></br>
              <RelationToDeceased
                name="relationshipToDeceased"
                value={record.relationshipToDeceased || ""}
                otherValue={record.relationshipToDeceasedOther || ""}
                error={errors.relationshipToDeceased || errors.relationshipToDeceasedOther}
                onFieldChange={onFieldChange}
              />
            </FormField>
          )}
        </FormStack>
      </Stack>
    </>
  );
};

const renderVulnerableInfoBox = () => {
  return (
    <Stack rowGap={2}>
      <Typography variant="body1">
        <b>What help is available?</b>
      </Typography>

      <Typography variant="body1">
        After a bereavement, many people find they require support with mental or physical health,
        debts or managing money, and other sensitive matters. Banks, Insurers, Utilities and other
        organisations can help, but only if people ask for it.
      </Typography>
      <Typography variant="body1">
        If you select ‘I feel vulnerable’, Settld will alert all companies transferring accounts
        into your name. The organisations will then follow up with you directly, using the contact
        details you provide.
      </Typography>
    </Stack>
  );
};

const renderIntestacyInfoBox = (serviceProvider: any) => {
  if (serviceProvider.customForm === "starling-bank") {
    return (
      <InfoBoxTrigger
        white
        width="720px"
        content={
          <Stack rowGap={2}>
            <Typography variant="body1">
              It’s important to be aware that if you aren’t the legal next of kin or an Executor,
              Starling Bank will need to confirm you have permission to look after the estate from
              the person who is.
            </Typography>

            <img src="/images/starling-intestacy.png" width="100%" alt="Laws of Intestacy" />
          </Stack>
        }
      />
    );
  }
  return (
    <InfoBoxTrigger
      white
      width="720px"
      content={
        <Stack rowGap={2}>
          <Typography variant="body1">
            <b>Please note:</b>
          </Typography>

          <Typography variant="body1">
            <p>
              As per the intestacy rules&nbsp; (when there is no will)&nbsp;, providers require a
              confirmation from the immediate next of kin.&nbsp;&nbsp;Please check who is the next
              of kin in the Family Tree&nbsp; hierarchy&nbsp; here: &nbsp;&nbsp;
              <a href="https://www.settld.care/what-if-theres-no-will-intestacy-rules/">
                What if there is no will
              </a>
            </p>
          </Typography>
          <Typography variant="body1">
            If you're not the immediate next of kin, please provide their full name, relation to
            deceased and address in the messages section of your dashboard, once the form is
            submitted.
          </Typography>
        </Stack>
      }
    />
  );
};

const renderRepresentativeInfoBox = () => {
  return (
    <Stack rowGap={4}>
      <Stack rowGap={2}>
        <Typography variant="body1">
          <b>What is a "personal representative"?</b>
        </Typography>

        <Typography variant="body1">
          A <b>personal representative</b> is the person who is legally entitled to administer the
          estate of the person who has died. This term is used to collectively refer to both{" "}
          <b>Executors</b> and <b>Administrators</b>.
        </Typography>
      </Stack>

      <Stack rowGap={2}>
        <Typography variant="body1">
          <b>Am I an "Executor" or an "Administrator"?</b>
        </Typography>

        <Typography variant="body1">
          Use the diagram below to determine what type of personal representative you are.
        </Typography>
      </Stack>

      <Box sx={{ alignSelf: "center" }}>
        <img
          src={personalRepresentative}
          alt="personal representative diagram"
          width="400"
          height="auto"
          style={{ maxWidth: "100%" }}
        />
      </Box>
    </Stack>
  );
};
